<template>
    <div>
        <header class="modal-card-head is-radiusless">
            <div class="level">
                <div class="level-left">
                    <div class="level-item">
                        <condo-icon icon="download" size="is-small" type="is-clink" />
                    </div>
                    <div class="level-item">
                        <h1 class="title is-6">Download document {{ document.documentId }}</h1>
                    </div>
                </div>
            </div>
        </header>
        <section class="modal-card-body">
            <n-input
                @keyup.enter="download"
                ref="inputRef"
                focus
                v-model:value="filename"
                type="text"
                placeholder="Enter a filename"
                :id="buildId('filename')"
            />
        </section>
        <footer class="modal-card-foot">
            <div class="is-flex is-justify-content-flex-end is-fullwidth gap-2">
                <condo-button @click="emit('close')" type="is-default" :id="buildId('close')"> Cancel </condo-button>
                <condo-button
                    :disabled="!filename.length"
                    :loading="isLoading"
                    icon-left="save"
                    @click="download"
                    :outlined="false"
                    type="is-primary"
                    :id="buildId('download')"
                >
                    Download
                </condo-button>
            </div>
        </footer>
    </div>
</template>

<script lang="ts" setup>
import { elementIdBuilder, openDownloadLink } from '@/shared';
import { type IDocument, type IEstate, generateDocumentFileName } from '@condo/domain';
import { NInput } from 'naive-ui';
import { computed, onMounted, ref } from 'vue';
import { getSignedDownloadUrl } from '../../api/basecamp.api';

// stolen from @condo/google
enum GDriveEntityType {
    PDF = 'application/pdf',
    MSWord = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    MSExcel = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

interface Props {
    document: IDocument;
    estate?: IEstate;
    actionIdPrefix?: string;
}
type Emits = (e: 'close') => void;

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const inputRef = ref<HTMLInputElement | null>(null);
const filename = ref<string>(props.estate ? generateDocumentFileName(props.estate, props.document) : props.document.filename);
const isLoading = ref(false);

const extension = computed(() => {
    if (props.document.mimeType === GDriveEntityType.PDF) {
        return 'pdf';
    }
    if (props.document.mimeType === GDriveEntityType.MSExcel) {
        return 'xls';
    }
    if (props.document.mimeType === GDriveEntityType.MSWord) {
        return 'doc';
    }

    return undefined;
});

onMounted(() => inputRef.value?.focus());

const download = async () => {
    if (!filename.value.length) {
        return;
    }

    isLoading.value = true;

    const adjustedFileName =
        !extension.value || filename.value.includes('.') || filename.value.endsWith(extension.value) ? filename.value : `${filename.value}.${extension.value}`;

    // Check if document is stored on Google Drive - these require signed URLs even if canBeDownloadedDirectly
    const isGoogleDrive = props.document.link?.includes('drive.google.com') || props.document.path?.includes('drive.google.com');

    // For Google Drive documents, always use getSignedDownloadUrl
    // For other documents that can be downloaded directly, fetch the link
    // Otherwise, use getSignedDownloadUrl
    try {
        if (!isGoogleDrive) {
            await fetch(props.document.link)
                .then(response => response.blob())
                .then(blob => {
                    openDownloadLink(URL.createObjectURL(blob), adjustedFileName);
                    emit('close');
                });
        } else {
            await getSignedDownloadUrl(props.document.uuid, adjustedFileName).then(({ url }) => {
                // Open signed URL in a new tab
                window.open(url, '_blank');
                emit('close');
            });
        }
    } finally {
        isLoading.value = false;
    }
};

const buildId = (id: string) => (props.actionIdPrefix ? elementIdBuilder(props.actionIdPrefix)(id) : undefined);
</script>
